'use client';

import React, { useMemo, useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { usePushToCRMMutation, useGetMappingsQuery } from 'src/api/redux';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import {
  useGetFieldDefinitionsQuery,
  useGetObjectDefinitionsListQuery,
  useUpdateMedRMStatusMutation,
} from 'src/api/redux/medrm';
import { useDeleteCRMLinkMutation } from 'src/api/redux/integrations';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { StatusButton } from './components';
import { Dialog, Typography, PulseButton } from 'src/components';

interface MedRMStatusProps {
  provider: MedScout.Opportunity;
  variant?: 'compact' | 'regular';
  testIdPrefix?: string;
}

/**
 * @description MedRMStatus component
 * @param provider provider object
 * @param variant compact or regular
 * @returns
 */
const MedRMStatus = ({
  provider,
  variant = 'regular',
  testIdPrefix = '',
}: MedRMStatusProps): React.ReactElement | null => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);
  const [currentStatus, setCurrentStatus] = useState({
    label: 'No Status',
    color: '#a7a7a7',
  });
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const menuId = useId();
  const [medRmLinkIdToReset, setMedRmLinkIdToReset] = useState<number | null>(
    null
  );
  const [localCrmLinkId, setLocalCrmLinkId] = useState<number | null>(null);

  const [deleteCRMLink] = useDeleteCRMLinkMutation();

  const { data: objectDefinitions, isLoading: isLoadingObjects } =
    useGetObjectDefinitionsListQuery();

  const { data: fieldDefinitions, isLoading: isLoadingFields } =
    useGetFieldDefinitionsQuery(
      {
        objectId: objectDefinitions?.results?.[0]?.id,
      },
      {
        skip: !objectDefinitions?.results?.[0]?.id,
      }
    );

  const [pushToCRM, { isLoading: isPushToCRM }] = usePushToCRMMutation();

  const [updateMedRMStatus, { isLoading: isUpdatingMedRmStatus }] =
    useUpdateMedRMStatusMutation();

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const hasStatus = useMemo(() => {
    return provider?.crm_links?.[0];
  }, [provider]);

  const crmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const mappings = useMemo(() => {
    return allMappings?.results?.filter(
      ({ medscout_object_type, enabled }) =>
        medscout_object_type?.toLowerCase() ===
          (provider?.content_type?.toLowerCase() ||
            provider?.type?.toLowerCase()) && enabled
    );
  }, [allMappings, provider]);

  const statusField = useMemo(() => {
    return fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
  }, [fieldDefinitions]);

  const statuses = useMemo(() => {
    // remove statuses with no bucket (precautionary measure)
    const hasBucket = statusField?.options?.filter((option) => option.bucket);

    // order by bucket putting todo at the top, in process in the middle, and done at the end, ordering each section by order
    // this maintains the order the user put them in
    hasBucket?.sort((a, b) => {
      if (a.bucket === b.bucket) {
        return a.order - b.order;
      }
      return a.bucket === 'todo' ? -1 : a.bucket === 'done' ? 1 : 0;
    });

    return hasBucket;
  }, [statusField]);

  useEffect(() => {
    if (!statuses || !hasStatus) return;

    const status = statuses?.find(
      (option) => option.value === provider?.crm_links[0]?.crm_status
    );

    if (status) {
      setCurrentStatus({
        label: status.label,
        color: status.color || '#a7a7a7',
      });
    }

    return () => {
      setCurrentStatus({
        label: 'No Status',
        color: '#32383E',
      });
    };
  }, [hasStatus, provider, statuses]);

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const handleShowMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!event || !event.currentTarget) return;
    event.stopPropagation();

    if (!isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const handleStatusUpdate = async (option: {
    value: string;
    label: string;
    color?: string;
  }) => {
    if (!option || !option.value) return;

    handleClose();
    // Check if provider has already been pushed to CRM
    // If so, just change the status
    // If not, push the provider to CRM and set the status
    if (!provider?.provider_id) return;

    const newStatus = {
      object_id: provider?.provider_id,
      object_type: mappings[0]?.medscout_object_type,
      mapping: mappings[0]?.id,
      fields: {
        status: option?.value,
      },
    };

    if (hasStatus) {
      if (!provider?.crm_links[0]?.id) return;

      try {
        await updateMedRMStatus({
          crmLinkId: provider?.crm_links[0]?.id,
          object_id: provider?.provider_id,
          fields: {
            status: option?.value,
          },
        }).unwrap();

        if (provider?.crm_links[0]?.crm_status === option?.value) {
          setCurrentStatus({
            label: option.label,
            color: option.color || '#a7a7a7',
          });
        }

        log.event('Update provider status', {
          ...provider,
        });
      } catch (error: any) {
        log.exception('Error updating provider status', {
          tags: {
            providerId: provider.provider_id,
            error,
          },
        });

        setNotification({
          title: 'Error',
          message: 'An error occurred while updating provider status',
          type: 'error',
        });
      }
    } else {
      try {
        const result = await pushToCRM(newStatus).unwrap();
        const newLinkId = result?.id;
        if (newLinkId) {
          setLocalCrmLinkId(newLinkId);
        }
        setCurrentStatus({
          label: option.label,
          color: option.color || '#a7a7a7',
        });
        log.event('Push to MedRM', {
          ...provider,
        });
      } catch (err: any) {
        log.exception('Error pushing provider to CRM', {
          tags: {
            providerId: provider.provider_id,
            error: err,
          },
        });

        setNotification({
          title: 'Error',
          message:
            err?.error_message ||
            'An error occurred while pushing provider to CRM',
          type: 'error',
        });
      }
    }
  };

  // --- Start: Updated Reset CRM Status Logic ---
  // Function to find the MedRM link ID and open the reset dialog
  const handleOpenResetDialog = () => {
    // Find the specific CRM link object where crm is 'MEDRM' (case-insensitive)
    const medRmLink = provider?.crm_links?.find(
      (link) => link.crm?.toUpperCase() === 'MEDRM'
    );

    // Fall back to localCrmLinkId when provider prop hasn't updated yet after pushToCRM
    const linkId = medRmLink?.id || localCrmLinkId;

    if (linkId) {
      setMedRmLinkIdToReset(linkId);
      handleClose();
      setIsDialogOpen(true);
      log.event('resetCRMStatusClicked', {
        ...provider,
        crmLinkId: linkId,
      });
    } else {
      // Handle case where MedRM link or its ID is not found (optional)
      log.event('resetCRMStatusWarning', {
        message: 'MedRM link ID not found for reset',
        ...provider,
        crmLinks: provider?.crm_links,
      });
      setNotification({
        title: 'Warning',
        message: 'Could not find the specific MedRM link to reset.',
        type: 'warning',
      });
    }
  };

  // Function to handle closing the reset dialog
  const handleResetDialogClose = () => {
    setIsDialogOpen(false);
    setMedRmLinkIdToReset(null); // Clear the stored ID when dialog closes
  };

  // Function to handle the confirmation of resetting CRM status
  const handleResetConfirm = async () => {
    setIsDialogOpen(false); // Close the dialog first
    if (!provider?.provider_id || !medRmLinkIdToReset) {
      log.event('resetCRMStatusPreconditionFailure', {
        message: 'Missing provider ID or MedRM Link ID for reset confirmation',
        ...provider,
        medRmLinkIdToReset,
      });
      return; // Exit if we don't have the necessary ID
    }

    const crmLinkId = medRmLinkIdToReset; // Use the stored ID

    try {
      // Call the RTK Query mutation to delete the CRM link
      // Cache invalidation is handled automatically by the API
      await deleteCRMLink({
        id: crmLinkId,
        object_id: provider?.provider_id,
      }).unwrap();

      // Reset local visual state immediately
      setCurrentStatus({ label: 'No Status', color: '#a7a7a7' });
      setLocalCrmLinkId(null);

      setNotification({
        title: 'Success',
        message: 'CRM status has been reset.',
        type: 'success',
      });
      log.event('resetCRMStatusConfirmed', {
        ...provider,
        crmLinkId,
      });
    } catch (error: any) {
      setNotification({
        title: 'Error',
        message:
          error?.error_message ||
          'An error occurred while resetting CRM status.',
        type: 'error',
      });
    } finally {
      setMedRmLinkIdToReset(null); // Clear the stored ID after attempt
    }
  };

  useClickOutside([menuRef, buttonRef], handleClose, isMenuOpen);

  // SSR-safe portal mounting
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Keyboard navigation - close on Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        handleClose();
        buttonRef.current?.querySelector('button')?.focus();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const isLoading =
    isLoadingObjects || isLoadingFields || isPushToCRM || isUpdatingMedRmStatus;

  const isMedRM = crmTypes[0]?.toLowerCase() === 'medrm';

  if (!provider || !mappings?.length || !isMedRM) return null;

  const testId = testIdPrefix ? `${testIdPrefix}-medrm-status` : 'medrm-status';

  const dropdownMenu = isMenuOpen && (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-label="Status options"
      className="fixed bg-white rounded-md shadow-lg z-[9999] w-60 max-h-[360px] overflow-auto py-1"
      style={{ top: menuPosition.top, left: menuPosition.left }}
      data-testid={`${testId}-menu`}
    >
      {statuses?.map((option, index) => (
        <button
          key={option.value || index}
          type="button"
          role="menuitem"
          onClick={() => handleStatusUpdate(option)}
          className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
          data-testid={`${testId}-option-${option.value}`}
        >
          <Icon
            icon={faCircle}
            className="text-sm"
            style={{ color: option?.color }}
          />
          <span>{option?.label}</span>
        </button>
      ))}
      {currentStatus?.label !== 'No Status' && (
        <>
          <div className="border-t border-neutral-200 my-1" role="separator" />
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenResetDialog}
            className="flex items-center w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-50 transition-colors"
            data-testid={`${testId}-reset-button`}
          >
            Reset CRM Status
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-center" data-testid={testId}>
        <div ref={buttonRef}>
          <StatusButton
            variant={variant}
            onClick={handleShowMenu}
            status={currentStatus}
            loading={isLoading}
            testIdPrefix={testIdPrefix}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-controls={isMenuOpen ? menuId : undefined}
          />
        </div>
      </div>
      {isMounted && createPortal(dropdownMenu, document.body)}
      <Dialog
        isOpen={isDialogOpen}
        onClose={handleResetDialogClose}
        className="max-h-48"
        showCloseButton
      >
        <Typography variant="h4" className="pl-4 pt-4 font-semibold">
          Reset CRM Status
        </Typography>
        <Dialog.Content className="pt-4 pl-4 pr-4 max-h-20">
          <Typography>
            Are you sure you want to reset the CRM Status for this provider?
            This action cannot be undone.
          </Typography>
        </Dialog.Content>
        <Dialog.Actions className="max-h-20">
          <PulseButton
            variant="soft"
            color="neutral"
            onClick={handleResetDialogClose}
          >
            Cancel
          </PulseButton>
          <PulseButton
            variant="solid"
            color="danger"
            onClick={handleResetConfirm}
          >
            Reset Status
          </PulseButton>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default MedRMStatus;
